import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Notifications,
  PersonAdd,
  Favorite,
  ChatBubble,
  Close,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { useNavigate } from 'react-router-dom';
import { getUserNotifications, markAsRead, markAllAsRead, Notification } from '../services/notificationsService';
import { pushNotificationService } from '../services/pushNotificationService';
import { createEvent, getUserAndInvitedEvents } from '../services/eventsService';
import {
  Button,
  Menu,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { CalendarToday, Restaurant, Share, FilterList as FilterIcon } from '@mui/icons-material';

export const NotificationsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  // Invite handling
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<Notification | null>(null);
  const [addingToCalendar, setAddingToCalendar] = useState(false);

  // Filter state
  const [activeFilter, setActiveFilter] = useState<'all' | 'social' | 'recipes' | 'events'>('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  // Carregar notificações do usuário em tempo real
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // Limpa o badge do ícone do app ao abrir a tela de notificações
    pushNotificationService.clearBadge();
    pushNotificationService.removeAllDeliveredNotifications();

    const unsubscribe = getUserNotifications(
      user.uid,
      50,
      (notifs) => {
        setNotifications(notifs);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read && notification.id) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
      }
    }

    // Navegação para notificações sociais
    if (notification.type === 'new_follower' && notification.senderId) {
      navigate(`/hostlyclub/profile/${notification.senderId}`);
      return;
    }

    if (notification.type === 'post_like' || notification.type === 'post_comment') {
      navigate('/hostly-club');
      return;
    }

    if (notification.type === 'post_share' && notification.metadata?.postId) {
      navigate(`/hostlyclub/post/${notification.metadata.postId}`);
      return;
    }

    if (notification.type === 'recipe_share') {
      navigate('/notifications/recipe-share', { state: { notification } });
      return;
    }

    if (notification.metadata?.guestId) {
      navigate(`/guests/${notification.metadata.guestId}`);
    } else if (notification.type === 'guest_invite' || notification.type === 'event_update' || (notification.metadata?.eventId && notification.title.toLowerCase().includes('convite'))) {
      // Show Invite/Event Dialog for invites AND event updates
      setSelectedInvite(notification);
      setShowInviteDialog(true);
    } else if (notification.metadata?.eventId) {
      navigate(`/events/${notification.metadata.eventId}`);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_follower':
        return <PersonAdd />;
      case 'post_like':
        return <Favorite />;
      case 'post_comment':
        return <ChatBubble />;
      case 'post_share':
        return <Share />;
      case 'recipe_share':
        return <Restaurant />;
      case 'guest_invite':
      case 'event_update':
      case 'rsvp_change':
      case 'event_reminder':
        return <Notifications />;
      default:
        return <Notifications />;
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.uid) return;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await markAllAsRead(user.uid);
    } catch {
      void 0;
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (activeFilter === 'all') return true;

    const socialTypes = ['post_like', 'post_comment', 'post_share', 'new_follower', 'post_mention'];
    const recipeTypes = ['recipe_share'];
    const eventTypes = ['guest_invite', 'event_update', 'rsvp_change', 'event_reminder'];

    if (activeFilter === 'social') return socialTypes.includes(notification.type);
    if (activeFilter === 'recipes') return recipeTypes.includes(notification.type);
    if (activeFilter === 'events') return eventTypes.includes(notification.type);

    return true;
  });

  const handleAddToCalendar = async () => {
    if (!selectedInvite || !user?.uid) return;

    try {
      setAddingToCalendar(true);

      // Eventos aceitos de convites NÃO contam no limite do usuário
      // Então não precisamos verificar o limite aqui

      // Try to extract data from metadata
      const meta = selectedInvite.metadata || {};

      if (meta.eventId) {
        const existing = await getUserAndInvitedEvents(user.uid);
        const alreadyHasAccess = existing.some(
          (e) => e.id === meta.eventId || e.originalEventId === meta.eventId
        );
        if (alreadyHasAccess) {
          setShowInviteDialog(false);
          setSelectedInvite(null);
          navigate('/events');
          return;
        }
      }

      // Fallback: we try to be smart, but really we rely on metadata
      // If metadata is partial (old invites), we might create a partial event

      const eventData = {
        title: meta.eventTitle || selectedInvite.title.replace('Você foi convidado para ', '').split('!')[0] || 'Novo Evento',
        description: selectedInvite.message, // Use the full message as description so they don't lose info
        date: meta.date || new Date().toISOString().split('T')[0], // Fallback to today if missing
        time: meta.time || '19:00',
        location: meta.location || '',
        guestCount: 0,
        status: 'planned' as const,
      };

      // Criar evento com flag de convite aceito (não conta no limite)
      await createEvent(user.uid, eventData, {
        isAcceptedInvite: true,
        originalEventId: meta.eventId,
        originalOwnerId: selectedInvite.senderId,
      });

      // Não precisa refreshLimits pois não incrementamos o contador

      setShowInviteDialog(false);
      setSelectedInvite(null);

      // Navigate to events or show success?
      navigate('/events');

    } catch (error) {
      console.error('Error adding to calendar', error);
    } finally {
      setAddingToCalendar(false);
    }
  };

  // Require authentication
  if (!authLoading && !user) {
    navigate('/login');
    return null;
  }

  return (
    <>
      <TopBar
        title="Notificações"
        icon={<Notifications sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        onBackClick={() => navigate(-1)}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: theme.radii.medium,
            boxShadow: theme.shadows.medium,
            mt: 0.5,
          }
        }}
      >
        <MenuItem onClick={() => { setActiveFilter('all'); setFilterAnchorEl(null); }}>Todas</MenuItem>
        <MenuItem onClick={() => { setActiveFilter('social'); setFilterAnchorEl(null); }}>Social</MenuItem>
        <MenuItem onClick={() => { setActiveFilter('recipes'); setFilterAnchorEl(null); }}>Receitas</MenuItem>
        <MenuItem onClick={() => { setActiveFilter('events'); setFilterAnchorEl(null); }}>Eventos</MenuItem>
      </Menu>

      <Box sx={{
        backgroundColor: theme.colors.background,
        minHeight: '100vh',
        paddingBottom: '120px',
        paddingTop: '70px',
      }}>
        {/* Filter Chips Layer */}
        <Box sx={{
          px: 2,
          py: 1,
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          backgroundColor: theme.colors.background,
        }}>
          {activeFilter !== 'all' && (
            <Chip
              label={
                activeFilter === 'social' ? 'Social' :
                  activeFilter === 'recipes' ? 'Receitas' : 'Eventos'
              }
              onDelete={() => setActiveFilter('all')}
              deleteIcon={<Close sx={{ fontSize: '14px !important' }} />}
              sx={{
                backgroundColor: theme.colors.secondary,
                color: theme.colors.primary,
                fontWeight: '500',
                '& .MuiChip-deleteIcon': {
                  color: theme.colors.primary,
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }
              }}
            />
          )}
          {activeFilter === 'all' && (
            <Chip
              label="Tudo"
              variant="filled"
              sx={{
                backgroundColor: `${theme.colors.primary}15`,
                color: theme.colors.primary,
                fontWeight: 'bold',
                border: `1px solid ${theme.colors.primary}30`,
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {notifications.some(n => !n.read) && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              sx={{ textTransform: 'none', color: theme.colors.primary, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Marcar todas como lidas
            </Button>
          )}
          <IconButton
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{ color: theme.colors.text.primary, p: 0.5 }}
          >
            <FilterIcon />
          </IconButton>
        </Box>
        <Container sx={{ mt: 2 }}>
          {/* Notifications List */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: theme.radii.medium,
              backgroundColor: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <List sx={{ padding: 0 }}>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: theme.colors.primary }} />
                </Box>
              ) : filteredNotifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Notifications sx={{ fontSize: '4rem', color: theme.colors.text.secondary, mb: 2 }} />
                  <Typography sx={{ color: theme.colors.text.secondary, fontSize: '1rem', mb: 1 }}>
                    {activeFilter === 'all'
                      ? 'Nenhuma notificação'
                      : `Nenhuma notificação de ${activeFilter === 'social' ? 'social' : activeFilter === 'recipes' ? 'receitas' : 'eventos'}`}
                  </Typography>
                </Box>
              ) : (
                filteredNotifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 2,
                        cursor: 'pointer',
                        backgroundColor: notification.read ? 'transparent' : `${theme.colors.primary}10`,
                        '&:hover': {
                          backgroundColor: `${theme.colors.primary}15`,
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            backgroundColor: notification.read ? theme.colors.border : theme.colors.secondary,
                            color: notification.read ? theme.colors.text.secondary : theme.colors.primary,
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            component="span"
                            sx={{
                              color: theme.colors.text.primary,
                              fontWeight: notification.read ? 'normal' : 'bold',
                              display: 'block',
                            }}
                          >
                            {notification.title}
                          </Typography>
                        }
                        secondary={
                          <Box component="span">
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ color: theme.colors.text.secondary, mb: 0.5, display: 'block', whiteSpace: 'pre-line' }}
                            >
                              {notification.message}
                            </Typography>
                            {notification.senderName && (
                              <Typography
                                variant="caption"
                                component="span"
                                sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', display: 'block' }}
                              >
                                De: {notification.senderName}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{
                                color: theme.colors.text.secondary,
                                fontSize: '0.7rem',
                                display: 'block',
                                mt: 0.5,
                              }}
                            >
                              {new Date(notification.createdAt).toLocaleDateString('pt-BR', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredNotifications.length - 1 && (
                      <Divider
                        variant="inset"
                        component="li"
                        sx={{ borderColor: theme.colors.border }}
                      />
                    )}
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        {/* Bottom Navigation */}
        <BottomNavigation />
      </Box>

      {/* Invite Details Dialog */}
      <Dialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
            maxWidth: '400px',
            width: '100%',
            m: 2
          }
        }}
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          {selectedInvite?.title || 'Detalhes do Convite'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{
            color: theme.colors.text.secondary,
            whiteSpace: 'pre-line', // Preserve line breaks from message
            mb: 2
          }}>
            {selectedInvite?.message}
          </DialogContentText>

          <Box sx={{
            p: 2,
            backgroundColor: `${theme.colors.primary}10`,
            borderRadius: theme.radii.medium,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <CalendarToday sx={{ color: theme.colors.primary }} />
            <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 'bold' }}>
              Deseja adicionar este evento ao seu calendário do Hostly?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowInviteDialog(false)}
            sx={{ color: theme.colors.text.secondary }}
          >
            Fechar
          </Button>
          <Button
            onClick={handleAddToCalendar}
            variant="contained"
            disabled={addingToCalendar}
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 }
            }}
          >
            {addingToCalendar ? <CircularProgress size={24} color="inherit" /> : 'Adicionar ao Calendário'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

